// client/src/components/admin/ProductManager.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './Manager.css';
import './ProductManager.css';
import { PRODUCT_CATEGORIES } from '../../config/categories';

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: PRODUCT_CATEGORIES[0],
    description: '',
    imageUrl: '',
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        category: product.category || PRODUCT_CATEGORIES[0],
        description: product.description || '',
        imageUrl: product.imageUrl || '',
      });
    } else {
      setFormData({
        name: '',
        category: PRODUCT_CATEGORIES[0],
        description: '',
        imageUrl: '',
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <form className="modal-content" onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? 'Edit Medical Product' : 'Add New Medical Product'}</h2>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="name">Product Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="e.g., Haemo-Dialysis Machine"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              {PRODUCT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="description">Clinical Description & Specifications</label>
            <textarea
              id="description"
              name="description"
              placeholder="Key clinical features, specifications, or package details..."
              value={formData.description}
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="form-group">
            <label htmlFor="imageUrl">Product Image URL</label>
            <input
              type="text"
              id="imageUrl"
              name="imageUrl"
              placeholder="https://example.com/image.jpg"
              value={formData.imageUrl}
              onChange={handleChange}
            />
            {formData.imageUrl && (
              <div className="image-preview">
                <p>Image Preview:</p>
                <img src={formData.imageUrl} alt="Preview" />
              </div>
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onCancel} className="btn-cancel">
            Cancel
          </button>
          <button type="submit" className="btn-save">
            <i className="fas fa-check"></i> Save Product
          </button>
        </div>
      </form>
    </div>
  );
};

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`);
      setProducts(response.data);
    } catch (err) {
      setError('Failed to fetch products.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSave = async (productData) => {
    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}` };

    const method = editingProduct ? 'put' : 'post';
    const url = editingProduct
      ? `${process.env.REACT_APP_API_URL}/api/admin/products/${editingProduct.id}`
      : `${process.env.REACT_APP_API_URL}/api/admin/products`;

    try {
      await axios({ method, url, data: productData, headers });
      closeModal();
      fetchProducts();
    } catch (err) {
      alert(`Error saving product: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const token = localStorage.getItem('authToken');
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/products/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchProducts();
      } catch (err) {
        alert('Error deleting product.');
      }
    }
  };

  const openModal = (product = null) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesCategory =
        selectedCategory === 'All' || p.category === selectedCategory;
      const term = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !term ||
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.category && p.category.toLowerCase().includes(term)) ||
        (p.description && p.description.toLowerCase().includes(term));
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchQuery]);

  const uniqueCategoriesCount = useMemo(() => {
    return new Set(products.map((p) => p.category).filter(Boolean)).size;
  }, [products]);

  return (
    <div className="manager-container">
      {isModalOpen && (
        <ProductForm product={editingProduct} onSave={handleSave} onCancel={closeModal} />
      )}

      <div className="manager-page-header">
        <div className="manager-title-group">
          <h1>Product Catalog Management</h1>
          <p>Add, update, and organize medical equipment and supplies across categories.</p>
        </div>
        <div className="manager-header-actions">
          <button type="button" className="btn-refresh" onClick={fetchProducts}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
          <button type="button" onClick={() => openModal()} className="btn-add">
            <i className="fas fa-plus"></i> Add New Product
          </button>
        </div>
      </div>

      {/* --- KPI Summary Cards --- */}
      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <div className="kpi-icon-box blue">
            <i className="fas fa-boxes"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Total Catalog Items</span>
            <span className="kpi-value">{products.length}</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box burgundy">
            <i className="fas fa-layer-group"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Active Categories</span>
            <span className="kpi-value">{uniqueCategoriesCount}</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box green">
            <i className="fas fa-filter"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Filtered Results</span>
            <span className="kpi-value">{filteredProducts.length}</span>
          </div>
        </div>
      </div>

      {/* --- Search & Category Filter Toolbar --- */}
      <div className="admin-toolbar">
        <div className="admin-search-box">
          <i className="fas fa-search"></i>
          <input
            type="search"
            placeholder="Search catalog by product name, category, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="admin-filter-group">
          <select
            className="admin-select-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="All">All Categories</option>
            {PRODUCT_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="manager-table-wrapper">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Product Details</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="admin-empty-cell">
                  <i className="fas fa-circle-notch fa-spin"></i>
                  Loading products...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="4" className="error-message">
                  {error}
                </td>
              </tr>
            ) : filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="4" className="admin-empty-cell">
                  <i className="fas fa-box-open"></i>
                  No matching products found.
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td>
                    <img
                      src={product.imageUrl || '/images/placeholder.png'}
                      alt={product.name}
                      className="product-thumbnail"
                    />
                  </td>
                  <td>
                    <span className="cell-primary-text">{product.name}</span>
                    {product.description && (
                      <span className="cell-secondary-text">{product.description}</span>
                    )}
                  </td>
                  <td>
                    <span className="category-pill-badge">{product.category}</span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button onClick={() => openModal(product)} className="btn-edit">
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="btn-delete"
                      >
                        <i className="fas fa-trash-alt"></i> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductManager;
